import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ExternalLink, Github, ArrowRight, CheckCircle2, Terminal, Layers, Activity, Cpu } from "lucide-react";
import TechTag from "../common/TechTag";

export default function FeaturedProjectCard({ project }) {
  const [activeTab, setActiveTab] = useState("preview");

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="group rounded-2xl border border-border/80 bg-gradient-to-b from-card/80 via-card/50 to-[#07070b] overflow-hidden shadow-2xl hover:border-primary/50 transition-all duration-300"
    >
      <div className="grid lg:grid-cols-12 gap-8 items-center p-6 sm:p-8 lg:p-10">
        
        {/* Left Column: Editorial & Technical Specifications */}
        <div className="lg:col-span-7 flex flex-col justify-between">
          <div>
            {/* Status and Project Index */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-primary font-medium">
                Featured Architecture // 01
              </span>
              <span className="font-mono text-muted-foreground/60 text-xs">/</span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 font-mono text-[10px] tracking-wider uppercase text-emerald-400">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Verified Production
              </span>
            </div>

            {/* Headline */}
            <h3 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-display text-foreground group-hover:text-primary transition-colors">
              <Link to="/works/vistara" className="focus-visible:ring-2 focus-visible:ring-primary rounded-sm">
                {project.name}
              </Link>
            </h3>

            {/* Tagline */}
            <p className="mt-2 text-lg sm:text-xl text-foreground/90 font-medium">
              Modular Web3 Infrastructure Landing Architecture
            </p>

            {/* Target Audience & Purpose */}
            <p className="mt-4 text-[14.5px] sm:text-[15.5px] text-foreground/80 leading-relaxed max-w-[62ch]">
              Engineered for Web3 protocol teams, node operators, and technical evaluators who require instant, zero-friction comprehension of modular network topology and developer tooling.
            </p>

            {/* Problem & Solution Synthesis */}
            <div className="mt-6 grid sm:grid-cols-2 gap-3.5 p-4 rounded-xl border border-border/60 bg-secondary/20">
              <div>
                <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-muted-foreground block mb-1">
                  The Friction
                </span>
                <p className="text-[13px] text-foreground/75 leading-relaxed">
                  Web3 docs often suffer from visual bloat, layout shifts, and inaccessible low-contrast interfaces.
                </p>
              </div>
              <div>
                <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-primary block mb-1">
                  The Engineering
                </span>
                <p className="text-[13px] text-foreground/75 leading-relaxed">
                  Atomic token engine delivering sub-second LCP, zero layout shifts, and WCAG AA accessibility.
                </p>
              </div>
            </div>

            {/* Stack Tags */}
            <div className="mt-6 flex flex-wrap gap-1.5">
              {project.stack.map((t) => (
                <TechTag key={t} label={t} />
              ))}
            </div>

            {/* Performance Benchmark Highlights */}
            <div className="mt-6 flex flex-wrap items-center gap-6 pt-5 border-t border-border/40 font-mono text-[11px] text-muted-foreground">
              <div className="flex items-center gap-2">
                <Activity className="h-3.5 w-3.5 text-emerald-400" />
                <span className="text-foreground font-medium">0.8s</span> LCP
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                <span className="text-foreground font-medium">0.00</span> CLS
              </div>
              <div className="flex items-center gap-2">
                <Layers className="h-3.5 w-3.5 text-primary" />
                <span className="text-foreground font-medium">WCAG 2.2</span> AA
              </div>
            </div>
          </div>

          {/* Action CTAs */}
          <div className="mt-8 flex flex-wrap items-center gap-3 sm:gap-3.5">
            <Link
              to="/works/vistara"
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-primary text-primary-foreground font-mono text-[11px] sm:text-[11.5px] tracking-[0.16em] sm:tracking-[0.18em] uppercase font-medium hover:bg-primary/90 transition-all shadow-[0_4px_16px_rgba(139,92,246,0.3)] focus-visible:ring-2 focus-visible:ring-primary w-full sm:w-auto"
            >
              Case Study <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            {project.liveUrl && (
              <a
                href={project.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-1.5 h-11 px-5 rounded-full border border-border/80 bg-card/60 hover:bg-secondary/60 hover:text-primary transition-colors font-mono text-[11px] sm:text-[11.5px] tracking-[0.16em] sm:tracking-[0.18em] uppercase text-foreground/90 focus-visible:ring-2 focus-visible:ring-primary w-full sm:w-auto"
              >
                Live Preview <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            {project.repoUrl && (
              <a
                href={project.repoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-1.5 h-11 px-4 rounded-full border border-border/80 bg-card/40 hover:bg-secondary/60 hover:text-foreground transition-colors font-mono text-[11.5px] tracking-[0.18em] uppercase text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary w-full sm:w-auto"
                title="View GitHub Repository"
              >
                <Github className="h-4 w-4" />
                <span className="sm:hidden font-mono text-[11px] uppercase">Source Code</span>
              </a>
            )}
          </div>
        </div>

        {/* Right Column: Visual Product & Architecture Preview */}
        <div className="lg:col-span-5">
          <div className="rounded-xl border border-border/80 bg-[#09080e] overflow-hidden shadow-2xl">
            {/* Window Chrome Header */}
            <div className="px-4 py-3 border-b border-border/70 bg-[#0f0e17] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-rose-500/80" />
                <span className="h-2.5 w-2.5 rounded-full bg-amber-500/80" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/80" />
                <span className="ml-2 font-mono text-[10.5px] text-muted-foreground/80">
                  vistara.network
                </span>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => setActiveTab("preview")}
                  className={`px-2 py-0.5 rounded font-mono text-[9.5px] tracking-wider uppercase transition-colors ${
                    activeTab === "preview"
                      ? "bg-primary/20 text-primary border border-primary/30"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Topology
                </button>
                <button
                  onClick={() => setActiveTab("code")}
                  className={`px-2 py-0.5 rounded font-mono text-[9.5px] tracking-wider uppercase transition-colors ${
                    activeTab === "code"
                      ? "bg-primary/20 text-primary border border-primary/30"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Architecture
                </button>
              </div>
            </div>

            {/* Preview Window Canvas */}
            <div className="p-5 sm:p-6 min-h-[340px] flex flex-col justify-between bg-gradient-to-b from-[#09080e] to-[#050508]">
              {activeTab === "preview" ? (
                <div className="space-y-4">
                  {/* Mock Web3 Hero Layout */}
                  <div className="p-4 rounded-lg border border-border/60 bg-[#12101b]">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-mono text-[10px] tracking-widest uppercase text-primary">
                        MODULAR EXECUTION
                      </span>
                      <span className="font-mono text-[9.5px] text-emerald-400 flex items-center gap-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> ACTIVE
                      </span>
                    </div>
                    <div className="text-[16px] font-medium text-foreground tracking-tight">
                      Decoupled Rollup Framework
                    </div>
                    <p className="text-[12px] text-muted-foreground mt-1 leading-relaxed">
                      Stateless client verification with zero-knowledge fraud proof submission.
                    </p>
                  </div>

                  {/* Dynamic Modular Network Grid Preview */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg border border-border/50 bg-[#12101b]/70">
                      <span className="font-mono text-[9.5px] text-muted-foreground block">
                        CONSENSUS LAYER
                      </span>
                      <span className="text-[13px] font-medium text-foreground/90 mt-0.5 block">
                        Tendermint Core
                      </span>
                      <span className="font-mono text-[9.5px] text-primary/80 mt-1 block">
                        Block: 420ms
                      </span>
                    </div>
                    <div className="p-3 rounded-lg border border-border/50 bg-[#12101b]/70">
                      <span className="font-mono text-[9.5px] text-muted-foreground block">
                        DATA AVAILABILITY
                      </span>
                      <span className="text-[13px] font-medium text-foreground/90 mt-0.5 block">
                        EigenDA / Celestia
                      </span>
                      <span className="font-mono text-[9.5px] text-emerald-400 mt-1 block">
                        Throughput: 15 MB/s
                      </span>
                    </div>
                  </div>

                  {/* Performance Telemetry Bar */}
                  <div className="p-3 rounded-lg border border-primary/20 bg-primary/5 flex items-center justify-between font-mono text-[10px]">
                    <span className="text-primary/90 font-medium">DESIGN TOKEN ENGINE</span>
                    <span className="text-foreground/80">Tailwind + CSS Variables</span>
                  </div>
                </div>
              ) : (
                /* Code / Spec View */
                <div className="font-mono text-[11px] text-primary/90 leading-relaxed bg-[#050508] p-4 rounded-lg border border-border/50 overflow-x-auto">
                  <div className="text-muted-foreground text-[10px] mb-2">// vistara-pipeline.config.ts</div>
                  <div><span className="text-violet-400">export const</span> vistaraStack = &#123;</div>
                  <div className="pl-3">framework: <span className="text-emerald-400">&quot;Next.js App Router&quot;</span>,</div>
                  <div className="pl-3">tokens: <span className="text-emerald-400">&quot;Atomic CSS Custom Properties&quot;</span>,</div>
                  <div className="pl-3">motion: <span className="text-emerald-400">&quot;Framer Motion (Reduced-Motion Aware)&quot;</span>,</div>
                  <div className="pl-3">lcpBudgetMs: <span className="text-amber-400">800</span>,</div>
                  <div className="pl-3">clsBudget: <span className="text-amber-400">0.02</span>,</div>
                  <div>&#125;;</div>
                </div>
              )}

              {/* Bottom Visual Terminal Status */}
              <div className="pt-3 border-t border-border/40 flex items-center justify-between font-mono text-[9.5px] text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Terminal className="h-3 w-3 text-primary" />
                  <span>fmi.dev/vistara-preview</span>
                </span>
                <span className="text-primary hover:underline cursor-pointer">
                  <Link to="/works/vistara">Inspect Case Study &rarr;</Link>
                </span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </motion.div>
  );
}
